import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { type ExtractedBudget } from "@/lib/offer-types";
import { useExtractOfferBudget } from "@/hooks/use-extract-offer-budget";
import { chunkErrorMessage } from "@/lib/chunk-reload";
import { useOfferLineTemplates } from "@/hooks/use-offers";
import { useImportBudget } from "@/hooks/use-import-budget";
import { enrichBudgetLines } from "@/lib/match-offer-line-templates";
import { cn } from "@/lib/utils";
import { SrIcon } from "@/components/ui/SrIcon";
import {
  buildPreviewFromMatchedBudget,
  useImportPreview,
} from "@/hooks/use-import-preview";
import { OfferPicker } from "@/components/new-project/OfferPicker";
import type { MatchedBudget } from "@/components/new-project/PdfOfferUpload";
import { ImportPreviewTable } from "./import/ImportPreviewTable";
import { ImportSummaryCard } from "./import/ImportSummaryCard";

interface ImportBudgetDialogProps {
  projectId: string;
  /** PDF-URL fra prosjektet (offer_pdf_url). Hvis satt vises "Bruk eksisterende"-knapp. */
  existingPdfUrl?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * VIB-586: Last opp tilbuds-PDF/bilde og opprett budget_lines mot eksisterende
 * prosjekt. Idempotent — re-kjøring oppdaterer matchende rader uten å
 * duplisere. Gjenbruker parser + matcher fra "Opprett prosjekt"-flyten.
 *
 * Støtter tre kilder:
 *   1. Eksisterende PDF lagret på prosjektet (offer_pdf_url)
 *   2. Ny PDF lastet opp av brukeren
 *   3. Bildefil (JPG/PNG) — OCR via tesseract.js, deretter samme parser
 */
export default function ImportBudgetDialog({
  projectId,
  existingPdfUrl,
  open,
  onOpenChange,
}: ImportBudgetDialogProps) {
  const { data: templates = [] } = useOfferLineTemplates();
  const importMut = useImportBudget();
  const extractBudget = useExtractOfferBudget();
  const parsing = extractBudget.isPending;

  const {
    step,
    previewLines,
    previewMetadata,
    setPreviewMetadata,
    unmatchedCount,
    reset,
    finalizePreview,
    updateLine,
    removeLine,
  } = useImportPreview();

  // VIB-1021-oppfølging: bruk samme OfferPicker-flyt som Nytt prosjekt — flere
  // budsjetter velges/kombineres med tilvalg-toggle, i stedet for å droppe alt
  // utenom det første budsjettet.
  const [multiOffers, setMultiOffers] = useState<MatchedBudget[]>([]);
  const [showPicker, setShowPicker] = useState(false);

  const resetAll = () => {
    reset();
    setMultiOffers([]);
    setShowPicker(false);
  };

  const handleClose = (next: boolean) => {
    if (!next) resetAll();
    onOpenChange(next);
  };

  /** Mal-match en ekstrahert budsjett til en MatchedBudget (som Nytt prosjekt). */
  const enrich = (offer: ExtractedBudget, prefix?: string): MatchedBudget => {
    const matched = enrichBudgetLines(offer.lines, templates);
    return {
      ...offer,
      name: prefix ? `${prefix} — ${offer.name}` : offer.name,
      lines: matched,
      unmatched_count: matched.filter((l) => l.template_match === null).length,
    };
  };

  /** Ett budsjett → preview direkte. Flere → la PL velge/kombinere i OfferPicker. */
  const finalizeOrPick = (offers: MatchedBudget[]) => {
    if (offers.length === 0) {
      toast.error("Ingen budsjettposter funnet.");
      return;
    }
    if (offers.length > 1) {
      setMultiOffers(offers);
      setShowPicker(true);
      return;
    }
    finalizeMatched(offers[0]);
  };

  const finalizeMatched = (budget: MatchedBudget) => {
    setShowPicker(false);
    const ok = finalizePreview(buildPreviewFromMatchedBudget(budget));
    if (!ok) toast.error("Ingen budsjettposter funnet.");
  };

  const handleExistingPdf = async () => {
    if (!existingPdfUrl) return;
    try {
      const res = await fetch(existingPdfUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const file = new File([blob], "tilbud.pdf", { type: "application/pdf" });
      const offers = await extractBudget.mutateAsync(file);
      finalizeOrPick(offers.map((o) => enrich(o)));
    } catch (err) {
      toast.error(`Kunne ikke hente eksisterende PDF: ${(err as Error).message}`);
    }
  };

  const handleFiles = async (files: File[]) => {
    if (files.length === 0) return;
    try {
      // Multi-fil: kall vision-parseren sekvensielt og samle alle budsjetter.
      // Bruker fil-navn som prefiks slik at OfferPicker kan skille kilder.
      const allOffers: MatchedBudget[] = [];
      for (const file of files) {
        const offers = await extractBudget.mutateAsync(file);
        const usePrefix = files.length > 1;
        for (const o of offers) {
          allOffers.push(enrich(o, usePrefix ? file.name.replace(/\.[^.]+$/, "") : undefined));
        }
      }
      finalizeOrPick(allOffers);
    } catch (err) {
      toast.error(`Feil ved analyse: ${chunkErrorMessage(err)}`);
    }
  };

  const handleConfirm = async () => {
    try {
      await importMut.mutateAsync({
        project_id: projectId,
        lines: previewLines.map((l) => ({
          phase: l.phase,
          description: l.description,
          quantity: l.quantity,
          unit: l.unit,
          quantity_hours: l.quantity_hours,
          amount: l.amount,
          cost_type: l.cost_type,
          template_id: l.template_id,
          is_optional: l.is_optional,
        })),
        metadata: previewMetadata,
      });
      handleClose(false);
    } catch {
      // toast handled in hook
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display text-lg">
            Importer budsjett fra tilbud
          </DialogTitle>
          <DialogDescription>
            {step === "upload"
              ? "Last opp tilbuds-PDF. Linjer matches automatisk mot maler og oppretter budsjettposter på prosjektet."
              : "Forhåndsvisning av parsede linjer. Korriger fase eller fjern linjer før import."}
          </DialogDescription>
        </DialogHeader>

        {step === "upload" && (
          <div className="flex-1 flex flex-col items-center justify-center min-h-[200px] gap-4">
            {parsing ? (
              <div className="flex flex-col items-center gap-3 p-8 border-2 border-dashed rounded-lg w-full">
                <SrIcon name="loader2" className="w-8 h-8 text-primary animate-spin" />
                <span className="text-sm font-medium">
                  {extractBudget.compressing
                    ? "Filen er stor — komprimerer før analyse…"
                    : "Claude Vision analyserer tilbudet…"}
                </span>
                <span className="text-[11px] text-muted-foreground">
                  {extractBudget.compressing ? "Dette kan ta litt tid" : "10–30 sekunder"}
                </span>
              </div>
            ) : showPicker && multiOffers.length > 1 ? (
              <OfferPicker offers={multiOffers} onSelect={finalizeMatched} />
            ) : (
              <>
                {existingPdfUrl && (
                  <button
                    type="button"
                    onClick={handleExistingPdf}
                    className="flex items-center gap-3 p-4 border-[1.5px] border-foreground rounded-lg w-full hover:bg-muted/30 transition-colors text-left"
                  >
                    <SrIcon name="file-text" className="w-6 h-6 text-primary shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">Bruk PDF som ligger på prosjektet</p>
                      <p className="text-[11px] text-muted-foreground truncate">
                        {existingPdfUrl}
                      </p>
                    </div>
                    <SrIcon name="arrow-right" className="w-4 h-4 text-muted-foreground" />
                  </button>
                )}
                <label
                  className={cn(
                    "flex flex-col items-center justify-center gap-3 p-8 border-2 border-dashed rounded-lg w-full cursor-pointer transition-colors hover:bg-muted/30",
                  )}
                >
                  <SrIcon name="upload" className="w-8 h-8 text-muted-foreground" />
                  <span className="text-sm font-medium">
                    {existingPdfUrl ? "…eller last opp ny fil" : "Velg tilbuds-PDF eller skjermbilde"}
                  </span>
                  <span className="text-xs text-muted-foreground text-center">
                    Claude Vision (AI) leser fila direkte — PDF, PNG eller JPG.
                  </span>
                  <input
                    type="file"
                    accept=".pdf,image/jpeg,image/png,image/webp"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      const files = Array.from(e.target.files ?? []);
                      if (files.length > 0) handleFiles(files);
                    }}
                  />
                </label>
              </>
            )}
          </div>
        )}

        {step === "preview" && (
          <div className="flex-1 overflow-y-auto -mx-6 px-6">
            {unmatchedCount > 0 && (
              <div className="mb-3 px-3 py-2 rounded-md border-[1.5px] border-foreground bg-[#FEF3C7] text-[11px] text-[#78350F]">
                {unmatchedCount} av {previewLines.length} linjer matchet ikke en mal —
                gul prikk markerer hvilke. Verifiser fasen før du bekrefter.
              </div>
            )}
            {previewMetadata && (
              <ImportSummaryCard
                metadata={previewMetadata}
                onChange={setPreviewMetadata}
              />
            )}
            {/* VIB-1021: budsjett-nivå tilvalg-overstyring. Vision forhåndsutfyller;
                PL kan markere hele importen som tilvalg (egen rubrikk på timetildeling). */}
            <label className="flex items-center gap-2 my-3 text-sm cursor-pointer">
              <Checkbox
                checked={previewLines.length > 0 && previewLines.every((l) => l.is_optional)}
                onCheckedChange={(v) => {
                  const next = v === true;
                  for (const l of previewLines) updateLine(l.id, { is_optional: next });
                }}
              />
              <span>Hele budsjettet er tilvalg/opsjon</span>
            </label>
            <ImportPreviewTable
              lines={previewLines}
              templates={templates}
              metadata={previewMetadata}
              onUpdate={updateLine}
              onRemove={removeLine}
            />
            <p className="text-[11px] text-muted-foreground mt-3">
              Re-kjøring av samme PDF oppdaterer eksisterende linjer (matchet på fase + beskrivelse) i
              stedet for å duplisere.
            </p>
          </div>
        )}

        <DialogFooter className="flex-row justify-between sm:justify-between gap-2 pt-3 border-t">
          {step === "preview" ? (
            <Button
              variant="ghost"
              onClick={resetAll}
              disabled={importMut.isPending}
            >
              <SrIcon name="arrow-left" className="w-4 h-4 mr-1.5" />
              Last opp ny
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={() => handleClose(false)}
              disabled={importMut.isPending || parsing}
            >
              Avbryt
            </Button>
            {step === "preview" && (
              <Button
                onClick={handleConfirm}
                disabled={importMut.isPending || previewLines.length === 0}
              >
                {importMut.isPending ? (
                  <SrIcon name="loader2" className="w-4 h-4 mr-1.5 animate-spin" />
                ) : (
                  <SrIcon name="check" className="w-4 h-4 mr-1.5" />
                )}
                Importer {previewLines.length} linjer
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
